import Icon from 'components/common/Icon';
import {
  initialRegistrationFormValues,
  RegistrationFormikValues,
  RegistrationValidationSchema,
} from 'constants/registration';
import { Formik, FormikHelpers } from 'formik';
import useWindowSize, { BREAKPOINTS } from 'hooks/useWindowSize';
import React, { useEffect, useRef, useState } from 'react';
import { scrollToTop } from 'utils/window';
import styles from './Registration.module.sass';
import { setProfileProperty, trackEvent } from 'services/analytics';
import { postCreatorApplication } from 'api/registration';
import Loader from 'components/common/Loader';
import useLocalStorage from 'hooks/useLocalStorage';
import FormikPersist from 'components/common/FormikPersist';
import { useRouter } from 'next/router';
import { APPLICATION_STATUSES, isCreator } from 'types/creator';
import ROUTES from 'config/routes';
import { useAuth } from 'hooks/useAuth';
import Button from 'components/common/Button';
import {
  alternateSignUpButtonLabel,
  alternateSignUpUrl,
  isAlternateSignUpEnabled,
} from 'constants/alternateSignUp';
// Dynamic Import for content that does not appear on first render
import dynamic from 'next/dynamic';
import Bugsnag, { addErrorMetaData } from 'services/bugsnag';
const CreatorProfileForm = dynamic(() => import('./CreatorProfileForm'));
const SocialAccounts = dynamic(() => import('./SocialAccounts'));
const ContentExamples = dynamic(() => import('./ContentExamples'));
const TextUpdates = dynamic(() => import('./TextUpdates'));
const OutsideUS = dynamic(() => import('./OutsideUS'));

enum FORM_STEP {
  BASIC_INFO,
  SOCIALS,
  CONTENT_EXAMPLES,
  SMS_NOTIFICATIONS,
  OUTSIDE_US,
}

const stepToStepName = (step: number) => {
  switch (step) {
    case FORM_STEP.BASIC_INFO:
      return 'Basic Information';
    case FORM_STEP.SOCIALS:
      return 'Social Accounts';
    case FORM_STEP.CONTENT_EXAMPLES:
      return 'Application Content';
    case FORM_STEP.OUTSIDE_US:
      return 'Outside US';
    default:
      return '';
  }
};

export type FormStatus = 'in-progress' | 'loading' | 'completed' | 'error';

const FORM_STEP_KEY = 'creator-registration-form-step';
const FORM_VALUES_KEY = 'creator-registration-form-values';

const RegistrationView: React.FC = () => {
  const { authProfile, isLoading } = useAuth();
  const router = useRouter();
  const [formStep, setFormStep] = useLocalStorage<FORM_STEP>(FORM_STEP_KEY, 0);
  const [status, setStatus] = useState<FormStatus>(
    isLoading ? 'loading' : 'in-progress'
  );
  const { width } = useWindowSize();
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const step = router.query.formStep;
    if (typeof step === 'string' && Object.keys(FORM_STEP).includes(step)) {
      const formStep = FORM_STEP[step as any] as any;
      setFormStep(formStep);
      return;
    }
    if (!isLoading) {
      if (router.query.user !== 'new') {
        const { creator } = authProfile!;
        if (isCreator(creator)) {
          const { applicationStatus } = creator;
          if (authProfile!.user.outsideUS) {
            setStatus('completed');
            setFormStep(FORM_STEP.OUTSIDE_US);
            return;
          }
          if (applicationStatus !== APPLICATION_STATUSES.NEW) {
            router.push(ROUTES.ApplicationStatus.getPath());
            return;
          }
        }
      }
      setStatus('in-progress');
      trackEvent('Creator Starts On Boarding Application', {
        formStep,
      });
    }
  }, []);

  const handleNextStep = () => {
    scrollToTop(formRef);
    setFormStep(formStep + 1);
  };

  const handlePreviousStep = () => {
    if (formStep <= 0) {
      return;
    }
    scrollToTop();
    setFormStep((prevState) => {
      const prevStep = prevState - 1;
      trackEvent(`Creator Returns to ${stepToStepName(prevStep)}`);
      return prevStep;
    });
  };

  const handleSubmit = async (
    values: RegistrationFormikValues,
    _helpers: FormikHelpers<RegistrationFormikValues>
  ) => {
    setStatus('loading');
    try {
      // NOTE: Verify in API that phone submission is ok to happen before this call
      const resp = await postCreatorApplication({
        ...values,
        user: {
          _id: authProfile!.user._id,
          email: authProfile!.user.email,
        },
      });

      trackEvent('Creator Completes Application', { values });
      if (values.address.country !== 'US') {
        setProfileProperty('Outside US', true);
        trackEvent(`Creator Is Outside US`, { id: resp.user._id });
        setFormStep(FORM_STEP.OUTSIDE_US);
        setStatus('completed');
      } else {
        setStatus('completed');
        router.push({
          pathname: ROUTES.ApplicationStatus.getPath(),
          query: {
            submission: true,
          },
        });
      }
      setStatus('in-progress');
    } catch (error) {
      Bugsnag.notify(
        'Application Submission Error',
        addErrorMetaData('Form', { values, error })
      );
      setStatus('error');
      // TODO: Error UI? Or window.alert pop up?
    }
  };

  const cFormStepper = (): JSX.Element => {
    switch (formStep) {
      case FORM_STEP.BASIC_INFO:
        return <CreatorProfileForm handleNextStep={handleNextStep} />;
      case FORM_STEP.SOCIALS:
        return <SocialAccounts handleNextStep={handleNextStep} />;
      case FORM_STEP.CONTENT_EXAMPLES:
        return <ContentExamples handleNextStep={handleNextStep} />;
      case FORM_STEP.SMS_NOTIFICATIONS:
        return <TextUpdates />;
      case FORM_STEP.OUTSIDE_US:
        return <OutsideUS />;
      default:
        return <></>;
    }
  };

  const greaterThanMD = width > BREAKPOINTS.MD;

  if (isAlternateSignUpEnabled && alternateSignUpUrl) {
    return (
      <div className={styles.formContainer}>
        <h1 className={styles.alternateTitle}>Apply to Trend</h1>
        <p className={styles.alternateSubtitle}>
          Continue to complete your creator application.
        </p>
        <Button
          type="primary"
          text={alternateSignUpButtonLabel}
          externalLink
          href={alternateSignUpUrl}
        />
      </div>
    );
  }

  return (
    <div className={styles.formContainer} ref={formRef}>
      {formStep < FORM_STEP.SMS_NOTIFICATIONS && (
        <div className={styles.backContainer} onClick={handlePreviousStep}>
          {formStep > 0 && (
            <Icon name="arrow-backward" className={styles.backIcon} />
          )}
          {greaterThanMD && (
            <p className={styles.backLabel}>Step {formStep + 1} of 4</p>
          )}
        </div>
      )}
      <Formik
        initialValues={
          {
            ...initialRegistrationFormValues,
            firstName: authProfile?.user.firstName || '',
            lastName: authProfile?.user.lastName || '',
          } as RegistrationFormikValues
        }
        validationSchema={RegistrationValidationSchema}
        onSubmit={handleSubmit}
      >
        <>
          <FormikPersist formKey={FORM_VALUES_KEY} />
          {status === 'loading' ? <FormPageLoader /> : cFormStepper()}
        </>
      </Formik>
    </div>
  );
};

export default RegistrationView;

export const FormPageLoader: React.FC = () => (
  <div className={styles.loadingContainer}>
    <Loader />
  </div>
);
